"use client";

import React, {Fragment} from "react";
import {useSearchParams} from "next/navigation";
import {Locale} from "~/i18n-config";
import {LangLink} from "~/components/locales/LangLink";
import {Menu,Transition} from "@headlessui/react";
import {FaArrowCircleRight} from "react-icons/fa";
import {RiAccountCircleFill} from "react-icons/ri";
import {IoMdBusiness} from "react-icons/io";
import {MdLocalHospital, MdOutlineManageAccounts, MdLogout} from "react-icons/md";
import clsx from "clsx";

export function AccountNavigation(
    { lang, dictionary, firstname, lastname, hasPermAdmin, hasPermAgent, hasPermBusiness, layout } :  { lang: Locale, dictionary: any, firstname: string, lastname: string, hasPermAdmin: boolean, hasPermAgent: boolean, hasPermBusiness: boolean, layout?: string | null }
) {

    const searchParams = useSearchParams();

    if(!firstname || firstname.length === 0 || !lastname || lastname.length === 0) {
        return (
            <Menu as="div" className="relative font-jost">
                {({ open, close }) => (
                    <>
                        <Menu.Button className={clsx(open ? "outline-white/20" : "outline-white hover:outline-white/50", "inline-flex flex-row gap-2 items-center justify-center box-border rounded-md py-2 px-4 text-normal font-jost font-semibold border-0 outline outline-offset-2 text-white transition-all duration-300")}>
                            <span className="text-left min-w-[120px] max-w-[220px] truncate">{dictionary.account}</span><FaArrowCircleRight size={"20px"} color="#ffffff" className={clsx(open ? "rotate-90" : "rotate-0", "transition-rotate duration-300")} />
                        </Menu.Button>
                        <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95">
                            <Menu.Items className="absolute left-0 right-0 z-[999] mt-4 w-full origin-top-right rounded-md shadow-lg outline outline-offset-0 outline-white">
                                <Menu.Item>
                                    <form action="/auth/logout" method="post">
                                        <input type="hidden" name="lang" value={lang} />
                                        <button className="group inline-flex items-center justify-start gap-2 w-full text-normal text-left font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150 rounded-b-md">
                                            <span className="flex group-hover:hidden shrink-0"><MdLogout size="20px" color="#ffffff" /></span>
                                            <span className="hidden group-hover:flex shrink-0"><MdLogout size="20px" color="#27272a" /></span>
                                            <span>{dictionary.navAccountLogout}</span>
                                        </button>
                                    </form>
                                </Menu.Item>
                            </Menu.Items>
                        </Transition>
                    </>
                )}
            </Menu>
        );
    }

    return (
        <Menu as="div" className="relative font-jost">
            {({ open, close }) => (
                <>
                    <Menu.Button className={clsx(open ? "outline-white/20" : "outline-white hover:outline-white/50", "inline-flex flex-row gap-2 items-center justify-center box-border rounded-md py-2 px-4 text-normal font-jost font-semibold border-0 outline outline-offset-2 text-white transition-all duration-300")}>
                        <span className="text-left min-w-[120px] max-w-[220px] truncate capitalize">{firstname} {lastname}</span><FaArrowCircleRight size={"20px"} color="#ffffff" className={clsx(open ? "rotate-90" : "rotate-0", "transition-rotate duration-300")} />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95">
                        <Menu.Items className="absolute left-0 right-0 z-[999] mt-4 w-full origin-top-right rounded-md shadow-lg outline outline-offset-0 outline-white">
                            <Menu.Item>
                                <LangLink
                                    lang={lang}
                                    href="/dashboard"
                                    onClick={close}
                                    className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150 rounded-t-md">
                                    <span className="flex group-hover:hidden shrink-0"><MdLocalHospital size="20px" color="#ffffff"/></span>
                                    <span className="hidden group-hover:flex shrink-0"><MdLocalHospital size="20px" color="#27272a"/></span>
                                    <span>{dictionary.navAccountHome}</span>
                                </LangLink>
                            </Menu.Item>
                            {hasPermBusiness && layout !== "business" && (
                                <Menu.Item>
                                    <LangLink
                                        lang={lang}
                                        href={{ pathname: "/business", query: Object.fromEntries(searchParams.entries()) }}
                                        onClick={close}
                                        className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150">
                                        <span className="flex group-hover:hidden shrink-0"><IoMdBusiness size="20px" color="#ffffff"/></span>
                                        <span className="hidden group-hover:flex shrink-0"><IoMdBusiness size="20px" color="#27272a"/></span>
                                        <span>{dictionary.navAccountBusiness}</span>
                                    </LangLink>
                                </Menu.Item>
                            )}
                            {(hasPermAdmin || hasPermAgent) && layout !== "management" && (
                                <Menu.Item>
                                    <LangLink
                                        lang={lang}
                                        href={"/management"}
                                        onClick={close}
                                        className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150">
                                        <span className="flex group-hover:hidden shrink-0"><MdOutlineManageAccounts size="20px" color="#ffffff"/></span>
                                        <span className="hidden group-hover:flex shrink-0"><MdOutlineManageAccounts size="20px" color="#27272a"/></span>
                                        <span>{dictionary.navAccountAdmin}</span>
                                    </LangLink>
                                </Menu.Item>
                            )}
                            <Menu.Item>
                                <LangLink
                                    lang={lang}
                                    href="/account"
                                    onClick={close}
                                    className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150">
                                    <span className="flex group-hover:hidden shrink-0"><RiAccountCircleFill size="20px" color="#ffffff"/></span>
                                    <span className="hidden group-hover:flex shrink-0"><RiAccountCircleFill size="20px" color="#27272a"/></span>
                                    <span>{dictionary.navAccountSettings}</span>
                                </LangLink>
                            </Menu.Item>
                            <Menu.Item>
                                <form action="/auth/logout" method="post">
                                    <input type="hidden" name="lang" value={lang} />
                                    <button className="group inline-flex items-center justify-start gap-2 w-full text-left font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150 rounded-b-md">
                                        <span className="flex group-hover:hidden shrink-0"><MdLogout size="20px" color="#ffffff" /></span>
                                        <span className="hidden group-hover:flex shrink-0"><MdLogout size="20px" color="#27272a"/></span>
                                        <span>{dictionary.navAccountLogout}</span>
                                    </button>
                                </form>
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );

}